import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

const USER_CATS = [
  { id: 'hands', label: 'Hands', color: '#FF5500' },
  { id: '4way', label: '4-Way', color: '#00B4D8' },
  { id: 'grooves', label: 'Grooves', color: '#7B2FBE' },
  { id: 'book', label: 'Book', color: '#F9A825' },
  { id: 'sheets', label: 'Sheets', color: '#22C55E' },
  { id: 'others', label: 'Others', color: '#EC4899' },
];

const BackIcon = () => (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>);
const PlusIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const TrashIcon = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>);
const ChevronRight = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>);
const CheckIcon = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>);

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const DAYS_SHORT = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const inputStyle = { width:'100%', background:'#1C1916', border:'1.5px solid #2E2924', borderRadius:8, color:'#F0EBE3', fontFamily:"'Barlow', sans-serif", fontSize:14, padding:'10px 12px', outline:'none' };
const labelStyle = { fontSize:11, color:'#7A7268', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', display:'block', marginBottom:6 };

export default function ProgramsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // If coming from exercise screen, pre-select that exercise
  const preselectedExercise = location.state?.exercise || null;

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [programExercises, setProgramExercises] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [editingPe, setEditingPe] = useState(null);
  const [editForm, setEditForm] = useState({ duration_minutes:10, target_bpm:'', days:[] });
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name, type: 'exercise'|'program' }

  const [progForm, setProgForm] = useState({ name:'', description:'', mode:'free', recurrence:'weekly', weeks:4, start_date: new Date().toISOString().split('T')[0] });
  // ensure weeks is always a number
  const setWeeks = (fn) => setProgForm(f => ({ ...f, weeks: fn(typeof f.weeks === 'number' ? f.weeks : 4) }));
  const [exForm, setExForm] = useState({
    exercise_id: preselectedExercise?.id ? String(preselectedExercise.id) : '',
    duration_minutes: 10,
    target_bpm: preselectedExercise?.default_bpm || '',
    days: [], // array of day indices for agenda mode
  });
  const [filterCat, setFilterCat] = useState('');

  useEffect(() => {
    loadPrograms();
    loadAllExercises();
    // If coming from exercise, go straight to add_exercise after picking a program
    if (preselectedExercise) setView('list');
  }, []);

  const loadPrograms = async () => {
    setLoading(true);
    const { data } = await supabase.from('programs').select('*').eq('user_id', user.id).order('created_at', { ascending:false });
    if (data) setPrograms(data);
    setLoading(false);
  };

  const loadAllExercises = async () => {
    const { data: exs } = await supabase.from('exercises').select('*, categories(name,color), subcategories(name)').eq('active', true).order('title');
    const { data: uexs } = await supabase.from('user_exercises').select('*, categories(name,color)').eq('user_id', user.id).order('title');
    const { data: cats } = await supabase.from('categories').select('*').order('sort_order');
    // Mark user exercises with prefix
    const userExs = (uexs || []).map(e => ({ ...e, id: `user-${e.id}`, isUserExercise: true }));
    if (exs) setAllExercises([...(exs || []), ...userExs]);
    if (cats) setCategories(cats);
  };

  const loadProgramExercises = async (programId) => {
    const { data } = await supabase.from('program_exercises')
      .select('*, exercises(id, title, image_url, default_bpm, difficulty, categories(name,color)), user_exercises(id, title, image_url, default_bpm, user_category)')
      .eq('program_id', programId)
      .order('day_of_week', { ascending: true, nullsFirst: true })
      .order('sort_order');
    if (data) setProgramExercises(data);
  };

  const createProgram = async () => {
    if (!progForm.name.trim()) return;
    setSaving(true);
    const { data } = await supabase.from('programs').insert({
      user_id: user.id, name: progForm.name, description: progForm.description, mode: progForm.mode,
      start_date: progForm.start_date || new Date().toISOString().split('T')[0],
      recurrence: progForm.mode === 'agenda' ? progForm.recurrence : null,
      weeks: progForm.recurrence === 'weekly' ? (parseInt(progForm.weeks) || null) : null,
    }).select().single();
    setSaving(false);
    if (data) { setPrograms(p => [data, ...p]); openProgram(data); }
  };

  const deleteProgram = (prog) => {
    setConfirmDelete({ id: prog.id, name: prog.name, type: 'program' });
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    if (confirmDelete.type === 'program') {
      await supabase.from('programs').delete().eq('id', confirmDelete.id);
      setPrograms(p => p.filter(x => x.id !== confirmDelete.id));
      if (selectedProgram?.id === confirmDelete.id) setView('list');
    } else if (confirmDelete.type === 'exercise') {
      await supabase.from('program_exercises').delete().eq('id', confirmDelete.id);
      setProgramExercises(p => p.filter(x => x.id !== confirmDelete.id));
    }
    setConfirmDelete(null);
  };

  const openProgram = async (prog) => {
    setSelectedProgram(prog);
    await loadProgramExercises(prog.id);
    // If coming from exercise screen, go straight to add
    if (preselectedExercise) {
      setView('add_exercise');
    } else {
      setView('detail');
    }
  };

  const toggleDay = (idx) => {
    setExForm(f => ({
      ...f,
      days: f.days.includes(idx) ? f.days.filter(d => d !== idx) : [...f.days, idx]
    }));
  };

  const addExercise = async () => {
    if (!exForm.exercise_id && !preselectedExercise) return;
    setSaving(true);

    const isAgenda = selectedProgram.mode === 'agenda';
    const rawId = exForm.exercise_id || preselectedExercise?.id || '';
    const isUserEx = String(rawId).startsWith('user-');
    const exId = isUserEx ? parseInt(String(rawId).replace('user-', '')) : parseInt(rawId);
    
    const baseData = {
      program_id: selectedProgram.id,
      exercise_id: isUserEx ? null : exId,
      user_exercise_id: isUserEx ? exId : null,
      duration_minutes: parseInt(exForm.duration_minutes) || 10,
      target_bpm: exForm.target_bpm ? parseInt(exForm.target_bpm) : null,
      sort_order: programExercises.length,
    };

    if (isAgenda && exForm.days.length > 0) {
      const inserts = exForm.days.map(day => ({ ...baseData, day_of_week: day }));
      await supabase.from('program_exercises').insert(inserts);
    } else {
      await supabase.from('program_exercises').insert({ ...baseData, day_of_week: null });
    }

    setSaving(false);
    setSuccess('Exercise added! ✓');
    setTimeout(() => setSuccess(''), 2000);
    setExForm(f => ({ ...f, exercise_id: preselectedExercise?.id ? String(preselectedExercise.id) : '', days: [] }));
    await loadProgramExercises(selectedProgram.id);
    if (!preselectedExercise) setView('detail');
  };

  const removeExercise = (pe) => {
    setConfirmDelete({ id: pe.id, name: pe.exercises?.title || 'exercise', type: 'exercise' });
  };

  const openEdit = (pe) => {
    setEditingPe(pe);
    setEditForm({
      duration_minutes: pe.duration_minutes || 10,
      target_bpm: pe.target_bpm || '',
      days: pe.day_of_week !== null && pe.day_of_week !== undefined ? [pe.day_of_week] : [],
    });
  };

  const saveEdit = async () => {
    if (!editingPe) return;
    setSaving(true);
    const dayVal = editForm.days.length > 0 ? editForm.days[0] : null;
    await supabase.from('program_exercises').update({
      duration_minutes: parseInt(editForm.duration_minutes) || 10,
      target_bpm: editForm.target_bpm ? parseInt(editForm.target_bpm) : null,
      day_of_week: dayVal,
    }).eq('id', editingPe.id);
    setSaving(false);
    setEditingPe(null);
    await loadProgramExercises(selectedProgram.id);
  };

  const filteredExercises = filterCat
    ? allExercises.filter(e => {
        if (e.isUserExercise) return e.user_category === filterCat;
        return e.category_id === parseInt(filterCat);
      })
    : allExercises;

  const byDay = {};
  programExercises.forEach(pe => {
    const key = pe.day_of_week !== null && pe.day_of_week !== undefined ? pe.day_of_week : 'free';
    if (!byDay[key]) byDay[key] = [];
    byDay[key].push(pe);
  });

  const totalMinutes = programExercises.reduce((a, pe) => a + (pe.duration_minutes || 0), 0);

  return (
    <div style={{ fontFamily:"'Barlow', sans-serif", background:'#111009', minHeight:'100vh', color:'#F0EBE3', maxWidth:'var(--app-width, 860px)', margin:'0 auto', paddingBottom:40 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700&family=Barlow+Condensed:wght@400;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        button { cursor: pointer; border: none; font-family: inherit; transition: all 0.15s; }
        button:active { transform: scale(0.96); opacity: 0.82; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        @keyframes slideDown { from{opacity:0;transform:translateY(-10px)} to{opacity:1;transform:none} }
        .fu { animation: fadeUp 0.25s ease forwards; }
        .bg-grid { position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: linear-gradient(rgba(255,85,0,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,85,0,0.02) 1px, transparent 1px);
          background-size: 40px 40px; }
        .prog-card { background:#1C1916; border:1px solid #2A2520; border-radius:12px; padding:16px; cursor:pointer; transition:all 0.18s; margin-bottom:10px; }
        .prog-card:hover { border-color:#3A3530; }
        .ex-pick { background:#1C1916; border:1px solid #2A2520; border-radius:10px; padding:11px 13px; display:flex; align-items:center; gap:10px; cursor:pointer; margin-bottom:6px; transition:all 0.15s; }
        .ex-pick:hover { border-color:#FF5500; }
        .ex-pick.selected { border-color:#FF5500; background:rgba(255,85,0,0.08); }
        .day-btn { padding:8px 0; border-radius:8px; font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:12px; letter-spacing:1px; transition:all 0.15s; flex:1; border:none; cursor:pointer; }
        .success-toast { position:fixed; top:20px; left:50%; transform:translateX(-50%); background:#22C55E; color:#fff; padding:10px 24px; border-radius:10px; font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:14px; letter-spacing:1px; z-index:999; animation:slideDown 0.3s ease; }
      `}</style>
      <div className="bg-grid"/>

      {success && <div className="success-toast">{success}</div>}

      {/* ── CONFIRM DELETE MODAL ── */}
      {confirmDelete && (
        <div style={{ position:'fixed', inset:0, zIndex:400, background:'rgba(0,0,0,0.9)', display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(6px)', padding:'0 20px' }}>
          <div style={{ background:'#171410', border:'1px solid #3A2020', borderRadius:16, padding:'28px 24px', width:'100%', maxWidth:340, textAlign:'center' }}>
            <div style={{ fontSize:44, marginBottom:12 }}>⚠️</div>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize:20, fontWeight:800, letterSpacing:1, color:'#F0EBE3', marginBottom:8 }}>
              ARE YOU SURE?
            </div>
            <div style={{ fontSize:14, color:'#7A7268', marginBottom:6, lineHeight:1.5 }}>
              You are about to delete
            </div>
            <div style={{ fontSize:15, fontWeight:700, color:'#FF5500', marginBottom:20, padding:'8px 12px', background:'rgba(255,85,0,0.1)', borderRadius:8 }}>
              "{confirmDelete.name}"
            </div>
            <div style={{ fontSize:12, color:'#5A5248', marginBottom:24 }}>
              {confirmDelete.type === 'program' ? 'All program exercises will be deleted.' : 'This action cannot be undone.'}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setConfirmDelete(null)} style={{ flex:1, padding:'13px', background:'#2A2520', color:'#C4B9AC', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:14, letterSpacing:1, border:'none', cursor:'pointer' }}>
                CANCELAR
              </button>
              <button onClick={confirmDeleteAction} style={{ flex:1, padding:'13px', background:'#CC2200', color:'#fff', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:14, letterSpacing:1, border:'none', cursor:'pointer' }}>
                APAGAR
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT MODAL ── */}
      {editingPe && (
        <div style={{ position:'fixed', inset:0, zIndex:300, background:'rgba(0,0,0,0.87)', display:'flex', alignItems:'flex-end', backdropFilter:'blur(4px)' }}>
          <div style={{ background:'#171410', borderRadius:'20px 20px 0 0', padding:'28px 20px 44px', width:'100%' }}>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize:20, fontWeight:800, letterSpacing:2, marginBottom:20, color:'#F0EBE3' }}>EDIT EXERCISE</div>
            <div style={{ fontSize:14, color:'#FF5500', fontWeight:600, marginBottom:16 }}>{editingPe.exercises?.title}</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom: selectedProgram?.mode==='agenda'?14:0 }}>
              <div>
                <label style={labelStyle}>Duration (min)</label>
                <input type="number" style={inputStyle} min={1} max={120} value={editForm.duration_minutes} onChange={e => setEditForm(f=>({...f,duration_minutes:e.target.value}))}/>
              </div>
              <div>
                <label style={labelStyle}>Target BPM</label>
                <input type="number" style={inputStyle} min={20} max={300} value={editForm.target_bpm} onChange={e => setEditForm(f=>({...f,target_bpm:e.target.value}))} placeholder="Opcional"/>
              </div>
            </div>
            {selectedProgram?.mode === 'agenda' && (
              <div style={{ marginBottom:24 }}>
                <label style={labelStyle}>Day of week</label>
                <div style={{ display:'flex', gap:4 }}>
                  {DAYS_SHORT.map((d, idx) => (
                    <button key={idx} className="day-btn" onClick={() => setEditForm(f => ({ ...f, days: f.days.includes(idx) ? f.days.filter(x=>x!==idx) : [...f.days, idx] }))}
                      style={{ background: editForm.days.includes(idx)?'#FF5500':'#2A2520', color: editForm.days.includes(idx)?'#fff':'#7A7268' }}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display:'flex', gap:10, marginTop:8 }}>
              <button onClick={() => setEditingPe(null)} style={{ flex:1, padding:'13px', background:'#2A2520', color:'#9A9088', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1, fontSize:14, border:'none', cursor:'pointer' }}>CANCEL</button>
              <button onClick={saveEdit} disabled={saving} style={{ flex:2, padding:'13px', background:'#FF5500', color:'#fff', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:800, letterSpacing:2, fontSize:14, border:'none', cursor:'pointer' }}>
                {saving ? 'SAVING…' : 'SAVE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── LIST ── */}
      {view === 'list' && (
        <>
          <div style={{ padding:'52px 20px 16px', background:'linear-gradient(180deg, #FF550010 0%, #111009 100%)', position:'relative', zIndex:1 }}>
            <button onClick={() => preselectedExercise ? navigate(-1) : navigate('/')} style={{ background:'transparent', color:'#7A7268', marginBottom:16, display:'flex', alignItems:'center', gap:6, fontSize:13, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1 }}>
              <BackIcon/> {preselectedExercise ? 'EXERCISE' : 'HOME'}
            </button>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <h1 style={{ fontFamily:"'Barlow Condensed'", fontSize:30, fontWeight:900, letterSpacing:1, color:'#F0EBE3', lineHeight:1 }}>
                  {preselectedExercise ? 'CHOOSE A PROGRAM' : 'MY PROGRAMS'}
                </h1>
                {preselectedExercise && (
                  <div style={{ fontSize:12, color:'#FF5500', marginTop:4, fontWeight:600 }}>
                    Adding: {preselectedExercise.title}
                  </div>
                )}
              </div>
              {!preselectedExercise && (
                <button onClick={() => { setProgForm({ name:'', description:'', mode:'free' }); setView('create'); }} style={{ background:'#FF5500', color:'#fff', width:42, height:42, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <PlusIcon/>
                </button>
              )}
            </div>
          </div>

          <div style={{ padding:'16px', position:'relative', zIndex:1 }}>
            {loading && <div style={{ textAlign:'center', padding:'40px 0', color:'#3A3530' }}>Loading…</div>}
            {!loading && programs.length === 0 && (
              <div style={{ textAlign:'center', padding:'60px 20px', color:'#3A3530' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>🎯</div>
                <div style={{ fontFamily:"'Barlow Condensed'", fontSize:18, fontWeight:700, letterSpacing:1, marginBottom:8 }}>NO PROGRAMS</div>
                <div style={{ fontSize:13, marginBottom:24 }}>Create your first training program</div>
                <button onClick={() => { setProgForm({ name:'', description:'', mode:'free' }); setView('create'); }} style={{ background:'#FF5500', color:'#fff', padding:'12px 28px', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:14, letterSpacing:1 }}>CREATE PROGRAM</button>
              </div>
            )}
            {programs.map((prog, i) => (
              <div key={prog.id} className="prog-card fu" style={{ animationDelay:`${i*0.04}s`, opacity:0 }} onClick={() => openProgram(prog)}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                  <div style={{ width:44, height:44, borderRadius:10, background:'rgba(255,85,0,0.12)', border:'1.5px solid rgba(255,85,0,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
                    {prog.mode === 'agenda' ? '📅' : '🎯'}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:15, fontWeight:700, color:'#F0EBE3', marginBottom:3 }}>{prog.name}</div>
                    {prog.description && <div style={{ fontSize:12, color:'#5A5248', marginBottom:6 }}>{prog.description}</div>}
                    <span style={{ fontSize:11, background: prog.mode==='agenda'?'rgba(0,180,216,0.15)':'rgba(255,85,0,0.12)', color: prog.mode==='agenda'?'#00B4D8':'#FF5500', padding:'2px 8px', borderRadius:6, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1 }}>
                      {prog.mode === 'agenda' ? 'SCHEDULE' : 'FREE'}
                    </span>
                  </div>
                  <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                    {!preselectedExercise && (
                      <button onClick={e => { e.stopPropagation(); deleteProgram(prog); }} style={{ background:'#2A1010', color:'#FF6666', width:30, height:30, borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center' }}><TrashIcon/></button>
                    )}
                    <div style={{ color:'#3A3530' }}><ChevronRight/></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── CREATE ── */}
      {view === 'create' && (
        <>
          <div style={{ padding:'52px 20px 20px', background:'linear-gradient(180deg, #FF550010 0%, #111009 100%)', position:'relative', zIndex:1 }}>
            <button onClick={() => setView('list')} style={{ background:'transparent', color:'#7A7268', marginBottom:16, display:'flex', alignItems:'center', gap:6, fontSize:13, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1 }}>
              <BackIcon/> PROGRAMS
            </button>
            <h1 style={{ fontFamily:"'Barlow Condensed'", fontSize:30, fontWeight:900, letterSpacing:1, color:'#F0EBE3' }}>NEW PROGRAM</h1>
          </div>
          <div style={{ padding:'20px 16px', position:'relative', zIndex:1 }}>
            <div style={{ marginBottom:16 }}>
              <label style={labelStyle}>Name *</label>
              <input style={inputStyle} value={progForm.name} onChange={e => setProgForm(f=>({...f,name:e.target.value}))} placeholder="Ex: Weekly Training, Rudiments…"/>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={labelStyle}>Description</label>
              <textarea style={{ ...inputStyle, resize:'vertical' }} rows={2} value={progForm.description} onChange={e => setProgForm(f=>({...f,description:e.target.value}))} placeholder="Optional description…"/>
            </div>
            <div style={{ marginBottom:24 }}>
              <label style={labelStyle}>Mode</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {[
                  { value:'free', icon:'🎯', title:'Free Mode', desc:'Practice whenever you want, no set days' },
                  { value:'agenda', icon:'📅', title:'Schedule Mode', desc:'Distribute exercises by day of the week' },
                ].map(m => (
                  <div key={m.value} onClick={() => setProgForm(f=>({...f,mode:m.value}))} style={{ background: progForm.mode===m.value?'rgba(255,85,0,0.1)':'#1C1916', border: `1.5px solid ${progForm.mode===m.value?'#FF5500':'#2A2520'}`, borderRadius:12, padding:'16px 14px', cursor:'pointer', transition:'all 0.15s' }}>
                    <div style={{ fontSize:24, marginBottom:8 }}>{m.icon}</div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#F0EBE3', marginBottom:4 }}>{m.title}</div>
                    <div style={{ fontSize:11, color:'#5A5248', lineHeight:1.4 }}>{m.desc}</div>
                  </div>
                ))}
              </div>
            </div>
            {progForm.mode === 'agenda' && (
              <div style={{ marginBottom:24 }}>
                <label style={labelStyle}>Start Date</label>
                <input type="date" style={inputStyle} value={progForm.start_date} onChange={e => setProgForm(f=>({...f,start_date:e.target.value}))}/>

                <label style={{ ...labelStyle, marginTop:14 }}>Recurrence</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
                  {[
                    { value:'weekly', label:'Nr. Weeks', icon:'🔄' },
                    { value:'monthly', label:'Monthly (this month only)', icon:'📅' },
                    { value:'forever', label:'Forever', icon:'∞' },
                  ].map(r => (
                    <div key={r.value} onClick={() => setProgForm(f=>({...f,recurrence:r.value}))}
                      style={{ background: progForm.recurrence===r.value?'rgba(255,85,0,0.1)':'#1C1916', border:`1.5px solid ${progForm.recurrence===r.value?'#FF5500':'#2A2520'}`, borderRadius:10, padding:'12px', cursor:'pointer', textAlign:'center', transition:'all 0.15s' }}>
                      <div style={{ fontSize:20, marginBottom:4 }}>{r.icon}</div>
                      <div style={{ fontSize:12, fontWeight:700, color:'#F0EBE3' }}>{r.label}</div>
                    </div>
                  ))}
                </div>

                {progForm.recurrence === 'weekly' && (
                  <div>
                    <label style={labelStyle}>Number of weeks</label>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <button onClick={() => setWeeks(w => Math.max(1, w-1))} style={{ background:'#2A2520', color:'#C4B9AC', width:36, height:36, borderRadius:8, fontSize:18, border:'none', cursor:'pointer' }}>−</button>
                      <div style={{ flex:1, textAlign:'center', fontFamily:"'Barlow Condensed'", fontSize:32, fontWeight:800, color:'#FF5500' }}>{typeof progForm.weeks === 'number' ? progForm.weeks : 4}</div>
                      <button onClick={() => setWeeks(w => Math.min(52, w+1))} style={{ background:'#2A2520', color:'#C4B9AC', width:36, height:36, borderRadius:8, fontSize:18, border:'none', cursor:'pointer' }}>+</button>
                    </div>
                    <div style={{ fontSize:11, color:'#5A5248', textAlign:'center', marginTop:6 }}>
                      Termina em {(() => { const d = new Date(progForm.start_date); d.setDate(d.getDate() + progForm.weeks * 7); const w = typeof progForm.weeks === 'number' ? progForm.weeks : 4; d.setDate(d.getDate() + w * 7); return d.toLocaleDateString('pt-PT'); })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            <button onClick={createProgram} disabled={!progForm.name.trim() || saving} style={{ width:'100%', padding:'14px', background:'#FF5500', color:'#fff', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:15, letterSpacing:2 }}>
              {saving ? 'CREATING…' : 'CREATE PROGRAM'}
            </button>
          </div>
        </>
      )}

      {/* ── DETAIL ── */}
      {view === 'detail' && selectedProgram && (
        <>
          <div style={{ padding:'52px 20px 20px', background:'linear-gradient(180deg, #FF550010 0%, #111009 100%)', position:'relative', zIndex:1 }}>
            <button onClick={() => setView('list')} style={{ background:'transparent', color:'#7A7268', marginBottom:16, display:'flex', alignItems:'center', gap:6, fontSize:13, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1 }}>
              <BackIcon/> PROGRAMS
            </button>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"'Barlow Condensed'", fontSize:11, fontWeight:700, letterSpacing:2, color:'#FF5500', marginBottom:4 }}>
                  {selectedProgram.mode === 'agenda' ? '📅 SCHEDULE' : '🎯 FREE MODE'}
                </div>
                <h1 style={{ fontFamily:"'Barlow Condensed'", fontSize:28, fontWeight:900, letterSpacing:1, color:'#F0EBE3', lineHeight:1 }}>{selectedProgram.name}</h1>
                <div style={{ fontSize:12, color:'#7A7268', marginTop:4 }}>{programExercises.length} exercises · {totalMinutes} min total</div>
              </div>
              <button onClick={() => { setExForm({ exercise_id:'', duration_minutes:10, target_bpm:'', days:[] }); setView('add_exercise'); }} style={{ background:'#FF5500', color:'#fff', padding:'10px 14px', borderRadius:10, display:'flex', alignItems:'center', gap:6, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:12, letterSpacing:1, flexShrink:0 }}>
                <PlusIcon/> ADICIONAR
              </button>
            </div>
          </div>

          <div style={{ padding:'16px', position:'relative', zIndex:1 }}>
            {programExercises.length === 0 && (
              <div style={{ textAlign:'center', padding:'40px 0', color:'#3A3530' }}>
                <div style={{ fontSize:36, marginBottom:10 }}>🥁</div>
                <div style={{ fontFamily:"'Barlow Condensed'", fontSize:15, fontWeight:700, letterSpacing:1 }}>NO EXERCISES</div>
                <div style={{ fontSize:12, marginTop:4 }}>Add exercises to your program</div>
              </div>
            )}

            {selectedProgram.mode === 'free' && (
              (byDay['free'] || []).map(pe => (
                <ProgramExRow key={pe.id} pe={pe} onDelete={() => removeExercise(pe)} onOpen={() => navigate(pe.user_exercise_id ? `/exercise/user-${pe.user_exercise_id}` : `/exercise/${pe.exercise_id}`, { state: { programSettings: { duration_minutes: pe.duration_minutes, target_bpm: pe.target_bpm } } })} onEdit={() => openEdit(pe)}/>
              ))
            )}

            {selectedProgram.mode === 'agenda' && (
              <>
                {DAYS.map((day, idx) => {
                  const dayExs = byDay[idx] || [];
                  return (
                    <div key={idx} style={{ marginBottom:16 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 4px', marginBottom:8 }}>
                        <div style={{ width:8, height:8, borderRadius:'50%', background: dayExs.length>0?'#FF5500':'#2A2520', flexShrink:0 }}/>
                        <span style={{ fontFamily:"'Barlow Condensed'", fontSize:13, fontWeight:800, color: dayExs.length>0?'#C4B9AC':'#3A3530', letterSpacing:1.5, flex:1 }}>{day.toUpperCase()}</span>
                        <span style={{ fontSize:11, color:'#3A3530' }}>{dayExs.length>0?`${dayExs.reduce((a,pe)=>a+(pe.duration_minutes||0),0)} min`:'—'}</span>
                      </div>
                      {dayExs.map(pe => (
                        <ProgramExRow key={pe.id} pe={pe} onDelete={() => removeExercise(pe)} onOpen={() => navigate(pe.user_exercise_id ? `/exercise/user-${pe.user_exercise_id}` : `/exercise/${pe.exercise_id}`, { state: { programSettings: { duration_minutes: pe.duration_minutes, target_bpm: pe.target_bpm } } })} onEdit={() => openEdit(pe)}/>
                      ))}
                    </div>
                  );
                })}
                {(byDay['free'] || []).length > 0 && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ padding:'6px 4px', marginBottom:8 }}>
                      <span style={{ fontFamily:"'Barlow Condensed'", fontSize:13, fontWeight:800, color:'#5A5248', letterSpacing:1.5 }}>NO DAY SET</span>
                    </div>
                    {(byDay['free'] || []).map(pe => (
                      <ProgramExRow key={pe.id} pe={pe} onDelete={() => removeExercise(pe)} onOpen={() => navigate(pe.user_exercise_id ? `/exercise/user-${pe.user_exercise_id}` : `/exercise/${pe.exercise_id}`, { state: { programSettings: { duration_minutes: pe.duration_minutes, target_bpm: pe.target_bpm } } })} onEdit={() => openEdit(pe)}/>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ── ADD EXERCISE ── */}
      {view === 'add_exercise' && (
        <>
          <div style={{ padding:'52px 20px 20px', background:'linear-gradient(180deg, #FF550010 0%, #111009 100%)', position:'relative', zIndex:1 }}>
            <button onClick={() => preselectedExercise ? setView('list') : setView('detail')} style={{ background:'transparent', color:'#7A7268', marginBottom:16, display:'flex', alignItems:'center', gap:6, fontSize:13, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1 }}>
              <BackIcon/> {selectedProgram?.name}
            </button>
            <h1 style={{ fontFamily:"'Barlow Condensed'", fontSize:26, fontWeight:900, letterSpacing:1, color:'#F0EBE3' }}>ADD EXERCISE</h1>
            {preselectedExercise && (
              <div style={{ fontSize:12, color:'#FF5500', marginTop:4, fontWeight:600 }}>Adding: {preselectedExercise.title}</div>
            )}
          </div>

          <div style={{ padding:'16px', position:'relative', zIndex:1 }}>
            {/* Settings */}
            <div style={{ background:'#1C1916', border:'1px solid #2A2520', borderRadius:12, padding:'16px', marginBottom:16 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom: selectedProgram?.mode==='agenda'?14:0 }}>
                <div>
                  <label style={labelStyle}>Duration (min)</label>
                  <input type="number" style={inputStyle} min={1} max={120} value={exForm.duration_minutes} onChange={e => setExForm(f=>({...f,duration_minutes:e.target.value}))}/>
                </div>
                <div>
                  <label style={labelStyle}>Target BPM</label>
                  <input type="number" style={inputStyle} min={20} max={300} value={exForm.target_bpm} onChange={e => setExForm(f=>({...f,target_bpm:e.target.value}))} placeholder="Opcional"/>
                </div>
              </div>

              {selectedProgram?.mode === 'agenda' && (
                <div>
                  <label style={labelStyle}>Dias da semana {exForm.days.length > 0 && <span style={{ color:'#FF5500' }}>({exForm.days.length} selecionado{exForm.days.length>1?'s':''})</span>}</label>
                  <div style={{ display:'flex', gap:4 }}>
                    {DAYS_SHORT.map((d, idx) => (
                      <button key={idx} className="day-btn" onClick={() => toggleDay(idx)}
                        style={{ background: exForm.days.includes(idx)?'#FF5500':'#2A2520', color: exForm.days.includes(idx)?'#fff':'#7A7268' }}>
                        {d}
                      </button>
                    ))}
                  </div>
                  {exForm.days.length === 0 && (
                    <div style={{ fontSize:11, color:'#5A5248', marginTop:6, fontStyle:'italic' }}>No day → stays in "no day set"</div>
                  )}
                </div>
              )}
            </div>

            {/* Exercise picker — hidden if preselected */}
            {!preselectedExercise && (
              <>
                <div style={{ display:'flex', gap:6, marginBottom:12, overflowX:'auto', paddingBottom:4, marginLeft:-16, paddingLeft:16, marginRight:-16, paddingRight:16 }}>
                  <button onClick={() => setFilterCat('')} style={{ padding:'5px 12px', borderRadius:20, flexShrink:0, background:!filterCat?'#FF5500':'#1C1916', color:!filterCat?'#fff':'#7A7268', border:`1px solid ${!filterCat?'#FF5500':'#2A2520'}`, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:11, letterSpacing:1 }}>ALL</button>
                  {categories.map(c => (
                    <button key={c.id} onClick={() => setFilterCat(filterCat===String(c.id)?'':String(c.id))} style={{ padding:'5px 12px', borderRadius:20, flexShrink:0, background:filterCat===String(c.id)?(c.color||'#FF5500'):'#1C1916', color:filterCat===String(c.id)?'#fff':'#7A7268', border:`1px solid ${filterCat===String(c.id)?(c.color||'#FF5500'):'#2A2520'}`, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:11, letterSpacing:1 }}>
                      {c.name.toUpperCase()}
                    </button>
                  ))}
                  {/* Separator */}
                  <div style={{ width:1, background:'#2A2520', flexShrink:0, margin:'0 4px' }}/>
                  {/* My Studies categories */}
                  {USER_CATS.filter(uc => allExercises.some(e => e.isUserExercise && e.user_category === uc.id)).map(uc => (
                    <button key={uc.id} onClick={() => setFilterCat(filterCat===uc.id?'':uc.id)} style={{ padding:'5px 12px', borderRadius:20, flexShrink:0, background:filterCat===uc.id?uc.color:'#1C1916', color:filterCat===uc.id?'#fff':'#7A7268', border:`1px solid ${filterCat===uc.id?uc.color:'#2A2520'}`, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:11, letterSpacing:1 }}>
                      ⭐ {uc.label.toUpperCase()}
                    </button>
                  ))}
                </div>
                <div style={{ marginBottom:80 }}>
                  {filteredExercises.map(ex => (
                    <div key={ex.id} className={`ex-pick${exForm.exercise_id===String(ex.id)?' selected':''}`}
                      onClick={() => setExForm(f=>({...f, exercise_id: f.exercise_id===String(ex.id)?'':String(ex.id)}))}>
                      {ex.image_url
                        ? <img src={ex.image_url} alt="" style={{ width:44, height:44, objectFit:'cover', objectPosition:'50% 24%', borderRadius:8, flexShrink:0 }}/>
                        : <div style={{ width:44, height:44, background:'#2A2520', borderRadius:8, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>🥁</div>
                      }
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'#F0EBE3' }}>{ex.title}</div>
                        <div style={{ fontSize:11, color:'#5A5248', marginTop:2 }}>
                          {ex.isUserExercise ? `⭐ ${USER_CATS.find(c=>c.id===ex.user_category)?.label||'My Studies'}` : ex.categories?.name} · ♩ {ex.default_bpm}
                        </div>
                      </div>
                      {exForm.exercise_id===String(ex.id) && (
                        <div style={{ width:20, height:20, borderRadius:'50%', background:'#FF5500', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><CheckIcon/></div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* If preselected exercise, show it */}
            {preselectedExercise && (
              <div style={{ background:'rgba(255,85,0,0.08)', border:'1.5px solid #FF5500', borderRadius:10, padding:'12px 14px', display:'flex', alignItems:'center', gap:12, marginBottom:80 }}>
                {preselectedExercise.image_url
                  ? <img src={preselectedExercise.image_url} alt="" style={{ width:44, height:44, objectFit:'cover', objectPosition:'50% 24%', borderRadius:8, flexShrink:0 }}/>
                  : <div style={{ width:44, height:44, background:'#2A2520', borderRadius:8, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>🥁</div>
                }
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'#F0EBE3' }}>{preselectedExercise.title}</div>
                  <div style={{ fontSize:11, color:'#FF5500', marginTop:2 }}>♩ {preselectedExercise.default_bpm} BPM</div>
                </div>
                <div style={{ width:20, height:20, borderRadius:'50%', background:'#FF5500', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><CheckIcon/></div>
              </div>
            )}

            {/* Add button fixed bottom */}
            <div style={{ position:'fixed', bottom:0, left:'50%', transform:'translateX(-50%)', width:'100%', maxWidth:'var(--app-width, 860px)', padding:'12px 16px 28px', background:'linear-gradient(to top, #111009 60%, transparent)', zIndex:50 }}>
              <button onClick={addExercise} disabled={(!exForm.exercise_id && !preselectedExercise) || saving} style={{ width:'100%', padding:'14px', background: (exForm.exercise_id||preselectedExercise)?'#FF5500':'#2A2520', color: (exForm.exercise_id||preselectedExercise)?'#fff':'#5A5248', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:15, letterSpacing:2 }}>
                {saving ? 'ADDING…' : 'ADD TO PROGRAM'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ProgramExRow({ pe, onDelete, onOpen, onEdit }) {
  const ex = pe.exercises || pe.user_exercises;
  if (!ex) return null;
  const isUserEx = !!pe.user_exercises;
  const color = ex.categories?.color || (isUserEx ? '#7B2FBE' : '#FF5500');
  const EditIcon2 = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
  return (
    <div style={{ background:'#1C1916', border:'1px solid #2A2520', borderRadius:10, padding:'12px 14px', display:'flex', alignItems:'center', gap:12, marginBottom:6 }}>
      {ex.image_url
        ? <img src={ex.image_url} alt="" style={{ width:44, height:44, objectFit:'cover', objectPosition:'50% 24%', borderRadius:8, flexShrink:0, cursor:'pointer' }} onClick={onOpen}/>
        : <div style={{ width:44, height:44, background:color+'18', borderRadius:8, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, cursor:'pointer' }} onClick={onOpen}>🥁</div>
      }
      <div style={{ flex:1, cursor:'pointer' }} onClick={onOpen}>
        <div style={{ fontSize:13, fontWeight:700, color:'#F0EBE3', marginBottom:3 }}>{ex.title}</div>
        <div style={{ display:'flex', gap:10, fontSize:11, flexWrap:'wrap' }}>
          <span style={{ color:'#FF5500', fontFamily:"'Barlow Condensed'", fontWeight:700 }}>⏱ {pe.duration_minutes}min</span>
          {pe.target_bpm && <span style={{ color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700 }}>♩ {pe.target_bpm} BPM</span>}
          {pe.day_of_week !== null && pe.day_of_week !== undefined && (
            <span style={{ color:'#00B4D8', fontFamily:"'Barlow Condensed'", fontWeight:700 }}>
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][pe.day_of_week]}
            </span>
          )}
        </div>
      </div>
      <div style={{ display:'flex', gap:5, flexShrink:0 }}>
        <button onClick={onEdit} style={{ background:'#2A2520', color:'#9A9088', width:30, height:30, borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center' }}><EditIcon2/></button>
        <button onClick={onDelete} style={{ background:'#2A1010', color:'#FF6666', width:30, height:30, borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center' }}><TrashIcon/></button>
      </div>
    </div>
  );
}
